import { useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { uploadFile } from "../api/dataMappingClient";
import { useChat } from "../state/useChat";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";

/**
 * Uploads a source file into the current session and reports the result the same way regardless of
 * which UI triggered it (the upload modal, or the inline Source-data drop zone) — one error/success
 * path instead of two copies drifting apart.
 */
export function useFileUpload({ onUploaded, onDone } = {}) {
  const { sessionId, setFile } = useSession();
  const { addMessage } = useChat();
  const { triggerRefresh } = useWorkingPanel();
  const toast = useToast();

  const handleFiles = useCallback(
    async (files) => {
      if (!sessionId || !files || files.length === 0) return;
      const file = files[0];
      try {
        const result = await uploadFile(sessionId, file);
        setFile(true, file.name);
        addMessage({ role: "system", content: `File uploaded: ${file.name}` });
        triggerRefresh();
        onDone?.();
        onUploaded?.({ frames: result?.dataframes || [], sourceName: file.name });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg =
          err?.response?.status === 409
            ? detail || "The assistant is working on this session. Try again in a moment."
            : detail || (err instanceof Error ? err.message : "Upload failed");
        addMessage({ role: "system", content: `Upload error: ${msg}`, isError: true });
        toast({ title: "Upload error", description: msg, status: "error", isClosable: true });
      }
    },
    [sessionId, setFile, addMessage, triggerRefresh, toast, onUploaded, onDone]
  );

  return { handleFiles };
}
